import { useEffect, useRef, type ReactNode } from "react";
import { useBackScreen } from "./screens";

/**
 * پنجره‌ی روی صفحه — انتخابِ چهره، فرشِ میز، خواندنِ حکم، پایانِ دست…
 *
 * پیش از این هر کدام یک `div.overlay` لُخت بودند و سه چیز کم داشتند:
 *
 * ۱. تمرکز پشتِ پرده می‌مانْد. با پنجره‌ی باز، Tab روی دکمه‌های زیرِ پرده
 *    می‌چرخید — چیزهایی که دیده هم نمی‌شدند. با صفحه‌کلید عملاً گم می‌شدی.
 * ۲. Esc کاری نمی‌کرد.
 * ۳. «بازگشت»ِ گوشی پنجره را نمی‌بست، کلِ بازی را می‌بست (نگاه کن به screens.ts).
 *
 * پنجره‌ی اجباری — آن‌که بازی تا انتخاب نکنی جلو نمی‌رود — `onClose` نمی‌گیرد:
 * نه Esc دارد، نه کلیکِ بیرون، و «بازگشت» هم رویش بی‌اثر است. ولی تمرکز همان‌جا
 * حبس می‌شود، چون آن‌جا هم تنها راهِ رسیدن به دکمه‌ها همین است.
 *
 * `card`: پایانِ دست و مسابقه. محتوا در کارتِ نتیجه می‌نشیند (شمسه‌ی بالا، تیترِ `.vt`) با
 * لحنِ برد، باخت یا خنثی؛ بردِ مسابقه جشن هم دارد (lux.css). دستِ میانی `className="minor"` می‌گیرد.
 *
 * `after`: چیزی که بیرونِ کارت و روی خودِ پرده می‌نشیند — آگهیِ پایانِ مسابقه ([[ResultAd]]).
 */
export function Overlay({ label, base = "overlay", className = "", card, onClose, after, children }: {
  label: string;
  /** کلاسِ پایه‌ی ظاهر. `veil` همان پرده‌ی تمام‌صفحه است و قاعده‌های خودش را دارد. */
  base?: "overlay" | "veil";
  className?: string;
  /** کارتِ نتیجه با این لحن — بالا را ببین. */
  card?: "win" | "lose" | "even";
  onClose?: () => void;
  after?: ReactNode;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);

  useBackScreen(true, () => {
    if (!onClose) return true;    // پنجره‌ی اجباری: بازگشت از رویش رد نمی‌شود
    onClose();
  });

  // تمرکزِ آغازین، و برگرداندنش سرِ جای اول وقتی پنجره بسته شد.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const el = box.current;
    (focusables(el)[0] ?? el)?.focus();
    return () => prev?.focus?.();
  }, []);

  return (
    <div
      ref={box}
      className={`${base} ${className}${card ? " result" : ""}`.trim()}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      tabIndex={-1}
      // پایینِ ماوس و نه کلیک: با کشیدنِ انتخاب از داخلِ پنجره به بیرون، رویدادِ
      // click روی پرده می‌نشیند و پنجره بی‌دلیل بسته می‌شد.
      onMouseDown={(e) => { if (onClose && e.target === e.currentTarget) onClose(); }}
      onKeyDown={(e) => {
        if (e.key === "Escape" && onClose) { e.stopPropagation(); onClose(); return; }
        if (e.key !== "Tab") return;
        const f = focusables(box.current);
        if (f.length === 0) return;
        const first = f[0], last = f[f.length - 1];
        const on = document.activeElement;
        // حلقه‌ی بسته: از آخری به اولی و برعکس، تا تمرکز از پنجره بیرون نرود.
        if (e.shiftKey && (on === first || on === box.current)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && on === last) { e.preventDefault(); first.focus(); }
      }}
    >
      {card ? (
        <div className={`vcard ${card}`}>
          <span className="vcard-crest" aria-hidden="true" />
          {children}
        </div>
      ) : children}
      {after}
    </div>
  );
}

const FOCUSABLE = "a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex='-1'])";

function focusables(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
}
